import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { adminTripsService } from '../../services/api';

interface AdminTripRequest {
  id: number;
  passenger_first_name: string;
  passenger_last_name: string;
  passenger_email: string;
  requested_seats: number;
  request_status: string;
  message: string | null;
  driver_message: string | null;
  total_price: string | null;
  created_at: string;
}

interface AdminTrip {
  id: number;
  start_location: string | null;
  destination: string | null;
  route_name: string | null;
  driver_first_name: string;
  driver_last_name: string;
  driver_email: string;
  departure_time: string;
  arrival_time: string | null;
  available_seats: number;
  price_per_seat: string | null;
  currency: string;
  trip_status: string;
  special_instructions: string | null;
  created_at: string;
  requests: AdminTripRequest[];
}

const statusColors: Record<string, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const Badge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
    {status}
  </span>
);

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '-');

const AdminTrips: React.FC = () => {
  const [trips, setTrips] = useState<AdminTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminTripsService.getTrips({
        page,
        limit: 20,
        status: status || undefined,
        search: search || undefined,
      });
      setTrips(data.trips || []);
      setPages(data.pagination?.pages || 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trips');
    } finally {
      setLoading(false);
    }
  }, [page, status, search]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Trips</h2>
        <button
          onClick={load}
          className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </button>
      </div>

      <form
        className="flex flex-wrap gap-3 mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setSearch(searchInput.trim());
        }}
      >
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search location or driver"
          className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
        <select
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="">All statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">
          Search
        </button>
      </form>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>}

      <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
        {loading ? (
          <div className="p-6 text-gray-500">Loading...</div>
        ) : trips.length === 0 ? (
          <div className="p-6 text-gray-500">No trips found.</div>
        ) : (
          trips.map((trip) => {
            const isOpen = expanded.has(trip.id);
            return (
              <div key={trip.id}>
                <button
                  onClick={() => toggle(trip.id)}
                  className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50"
                >
                  {isOpen ? <ChevronDown className="h-5 w-5 mt-0.5" /> : <ChevronRight className="h-5 w-5 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">
                        #{trip.id} {trip.start_location || '-'} → {trip.destination || '-'}
                      </span>
                      <Badge status={trip.trip_status} />
                    </div>
                    <div className="text-sm text-gray-500">
                      {trip.driver_first_name} {trip.driver_last_name} · {formatDate(trip.departure_time)} ·{' '}
                      {trip.requests.length} request{trip.requests.length === 1 ? '' : 's'}
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 pl-12 space-y-4">
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div><dt className="inline text-gray-500">Route: </dt><dd className="inline">{trip.route_name || '-'}</dd></div>
                      <div><dt className="inline text-gray-500">Driver email: </dt><dd className="inline">{trip.driver_email}</dd></div>
                      <div><dt className="inline text-gray-500">Departure: </dt><dd className="inline">{formatDate(trip.departure_time)}</dd></div>
                      <div><dt className="inline text-gray-500">Arrival: </dt><dd className="inline">{formatDate(trip.arrival_time)}</dd></div>
                      <div><dt className="inline text-gray-500">Available seats: </dt><dd className="inline">{trip.available_seats}</dd></div>
                      <div><dt className="inline text-gray-500">Price/seat: </dt><dd className="inline">{trip.price_per_seat ? `${trip.currency} ${trip.price_per_seat}` : '-'}</dd></div>
                      <div><dt className="inline text-gray-500">Created: </dt><dd className="inline">{formatDate(trip.created_at)}</dd></div>
                      {trip.special_instructions && (
                        <div className="sm:col-span-2"><dt className="inline text-gray-500">Instructions: </dt><dd className="inline">{trip.special_instructions}</dd></div>
                      )}
                    </dl>

                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Trip requests</h4>
                      {trip.requests.length === 0 ? (
                        <p className="text-sm text-gray-500">No requests for this trip.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="text-left text-gray-500 border-b">
                                <th className="py-2 pr-4">Passenger</th>
                                <th className="py-2 pr-4">Seats</th>
                                <th className="py-2 pr-4">Price</th>
                                <th className="py-2 pr-4">Status</th>
                                <th className="py-2 pr-4">Requested</th>
                                <th className="py-2 pr-4">Messages</th>
                              </tr>
                            </thead>
                            <tbody>
                              {trip.requests.map((r) => (
                                <tr key={r.id} className="border-b last:border-0 align-top">
                                  <td className="py-2 pr-4">
                                    {r.passenger_first_name} {r.passenger_last_name}
                                    <div className="text-xs text-gray-500">{r.passenger_email}</div>
                                  </td>
                                  <td className="py-2 pr-4">{r.requested_seats}</td>
                                  <td className="py-2 pr-4">{r.total_price ? `${trip.currency} ${r.total_price}` : '-'}</td>
                                  <td className="py-2 pr-4"><Badge status={r.request_status} /></td>
                                  <td className="py-2 pr-4">{formatDate(r.created_at)}</td>
                                  <td className="py-2 pr-4 text-xs text-gray-600">
                                    {r.message && <div>Passenger: {r.message}</div>}
                                    {r.driver_message && <div>Driver: {r.driver_message}</div>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 border border-gray-300 rounded-md bg-white disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-gray-600">Page {page} of {pages}</span>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border border-gray-300 rounded-md bg-white disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminTrips;
